import { RendererStyleFlags2 } from '@angular/core';
import { createSelector } from '@ngrx/store';
import { eventTypeInfoMap } from 'src/app/global.constants';
import { getAvailabilityLevel } from 'src/app/shared/availability-level';

import { DateHighlight, DateStyle } from 'src/app/shared/directives/highlight-dates.directive';

import { dayjs } from '../../../core/dayjs/dayjs';
import { selectSystemDetails } from '../../../state/branding.store';
import { selectJob } from '../../job-tool.reducer';

import { overviewTimelineFeature } from './overview-timeline.feature';

export const selectTimelineRange = createSelector(
    overviewTimelineFeature.selectTimeline,
    (timeline): string[] => {
        if (timeline.length === 0) return [];
        if (timeline.length === 1) return [timeline[0]];

        const start = dayjs(timeline[0]);
        const end = dayjs(timeline[1]);
        const dates: string[] = [];

        let current = start;
        while (current.isBefore(end) || current.isSame(end, 'day')) {
            dates.push(current.format('YYYY-MM-DD'));
            current = current.add(1, 'day');
        }

        return dates;
    },
);

export const selectTimelineHighlightDates = createSelector(
    selectJob,
    overviewTimelineFeature.selectViewDatesMap,
    selectSystemDetails,
    (job, viewDatesMap, systemDetails) => {

        const timezone = systemDetails?.timezone || job?.zone?.timezone;

        if (!timezone) return [];

        const colorsByDate = new Map<string, string[]>(); // date -> colors[]
        const datesMap = new Map<string, DateHighlight>();

        const getOrCreateDateHighlight = (date: string): DateHighlight => {
            if (!datesMap.has(date)) {
                datesMap.set(date, { date });
            }
            return datesMap.get(date)!;
        };

        const toFiniteNumber = (value: unknown): number => {
            const numericValue = Number(value);
            return Number.isFinite(numericValue) ? numericValue : 0;
        };

        // 1. Job Creation Date
        if (job?.createdAt) {
            const created = dayjs(job.createdAt * 1000).tz(timezone);
            const createdDateStr = created.format('YYYY-MM-DD'); // Correct format

            const highlight = getOrCreateDateHighlight(createdDateStr);
            highlight.styles = highlight.styles || [];
            highlight.styles.push({
                style: 'text-decoration',
                value: 'underline',
            });
        }

        // 2. Job Events Dots
        for (const { start, type } of job?.events || []) {
            if (!start) continue;

            const d = dayjs(start * 1000).tz(timezone);
            const dateStr = d.format('YYYY-MM-DD');

            const color = eventTypeInfoMap[type]?.backgroundColor;
            if (!color) continue;

            let colors = colorsByDate.get(dateStr);
            if (!colors) {
                colors = [];
                colorsByDate.set(dateStr, colors);
            }

            if (colors.length < 3) {
                colors.push(color);
            }
        }

        for (const [date, colors] of Array.from(colorsByDate.entries())) {
            // We have up to 3 colors, we want to distribute them across 5 slots.
            const slots: (string | undefined)[] = new Array(5).fill(undefined);

            switch (colors.length) {
                case 1:
                    slots[2] = colors[0];
                    break;
                case 2:
                    slots[1] = colors[0];
                    slots[3] = colors[1];
                    break;
                case 3:
                default:
                    slots[0] = colors[0];
                    slots[2] = colors[1];
                    slots[4] = colors[2];
                    break;
            }

            const styles: DateStyle[] = slots.map((color, index) => ({
                style: `--dot-color-${index + 1}`,
                value: color ?? null,
                flags: RendererStyleFlags2.DashCase,
            }));

            // Keep your overflow: visible trick
            styles.push({
                style: 'overflow',
                value: 'visible',
            });

            const highlight = getOrCreateDateHighlight(date);
            highlight.styles = (highlight.styles || []).concat(styles);
            highlight.classes = (highlight.classes || []).concat(['date-has-dots']);
        }

        // 3. Utilization Borders + Pricing Tier Indicator
        for (const dayData of Object.values(viewDatesMap)) {
            const truckCapacity = toFiniteNumber(dayData.truckCapacity);
            const actualTrucksUsed = toFiniteNumber(dayData.actualTrucksUsed);
            const truckUtilization = truckCapacity > 0 ? (actualTrucksUsed / truckCapacity) : 0;

            const crewCapacity = toFiniteNumber(dayData.crewCapacity);
            const actualCrewUsed = toFiniteNumber(dayData.actualCrewUsed);
            const actualCrewRequired = toFiniteNumber(dayData.actualCrewRequired);
            const crewRequired = Math.max(actualCrewUsed, actualCrewRequired);
            const crewUtilization = crewCapacity > 0 ? (crewRequired / crewCapacity) : 0;

            const maxUtilRaw = Math.max(truckUtilization, crewUtilization);
            const maxUtil = Number.isFinite(maxUtilRaw) ? Math.max(0, maxUtilRaw) : 0;
            const hasCapacity = truckCapacity > 0 || crewCapacity > 0;
            const availabilityRatio = hasCapacity ? Math.max(0, 1 - maxUtil) : 0;
            const availabilityLevel = getAvailabilityLevel(availabilityRatio, 1);
            const utilizationColor = availabilityLevel === 'high'
                ? 'var(--blue-500)'
                : availabilityLevel === 'medium'
                    ? 'var(--orange-500)'
                    : 'var(--red-500)';

            // Requirement: Utilization border should show for all dates even if util is 0
            const highlight = getOrCreateDateHighlight(dayData.date);

            // Requirement: Round to nearest dashed line (8 lines total)
            const steps = Math.round(maxUtil * 8);
            const percent = (steps / 8) * 100;

            // Use the tier colour for the utilization ring when a forecast tier is
            // available; fall back to the availability-based blue/orange/red otherwise.
            const tierColor = (dayData as any).prophet?.discount?.tier?.color;
            const ringColor = (typeof tierColor === 'string' && tierColor.trim().length > 0)
                ? tierColor
                : utilizationColor;

            const styles: DateStyle[] = [
                {
                    style: '--utilization',
                    value: `${Math.min(100, percent)}%`,
                    flags: RendererStyleFlags2.DashCase,
                },
                {
                    style: '--utilization-color',
                    value: ringColor,
                    flags: RendererStyleFlags2.DashCase,
                },
                {
                    style: 'overflow',
                    value: 'visible',
                }
            ];

            highlight.styles = (highlight.styles || []).concat(styles);

            highlight.classes = highlight.classes || [];
            if (!highlight.classes.includes('utilization-border')) {
                highlight.classes.push('utilization-border');
            }
        }

        return Array.from(datesMap.values());
    },
);
